import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { styled } from '@stitches/react';
import LanguageSwitcher from '../common/LanguageSwitcher';

// 底部容器样式
const FooterContainer = styled('footer', {
  width: '100%',
  height: '100px', // 设置固定高度为300px
  backgroundColor: '#262B44', // 统一使用深蓝色背景，与移动端保持一致
  borderTop: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  padding: 'var(--spacing-xl) 0 var(--spacing-xl)',
  
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-xl) 0'
  }
});

// 底部内容样式
const FooterContent = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 'var(--spacing-xl)',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'var(--spacing-xl) var(--spacing-lg)',
    padding: '0 var(--spacing-xl)'
  },
  
  '@media (max-width: 480px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  }
});

// 底部栏目样式
const FooterColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  
  '@media (max-width: 768px)': {
    gap: 'var(--spacing-sm)'
  }
});

// 底部标题样式
const FooterTitle = styled('h3', {
  color: '#8E92A3', // 统一使用浅灰色标题，与移动端一致
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-bold)',
  marginBottom: 'var(--spacing-sm)',
  
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-sm)',
    marginBottom: 'var(--spacing-md)',
    fontWeight: 'normal',
    letterSpacing: '1px'
  }
});

// 底部链接样式
const FooterLink = styled(Link, {
  color: 'white', // 统一使用白色文本，与移动端一致
  textDecoration: 'none',
  fontSize: 'var(--font-size-sm)',
  
  '&:hover': {
    color: 'var(--primary-color)'
  },
  
  '@media (max-width: 768px)': {
    marginBottom: 'var(--spacing-sm)',
    fontSize: '14px'
  }
});

// 底部外部链接样式
const FooterExternalLink = styled('a', {
  color: 'white', // 统一使用白色文本，与移动端一致
  textDecoration: 'none',
  fontSize: 'var(--font-size-sm)',
  
  '&:hover': {
    color: 'var(--primary-color)'
  },
  
  '@media (max-width: 768px)': {
    marginBottom: 'var(--spacing-sm)',
    fontSize: '14px'
  }
});

// 社交媒体链接容器样式
const SocialLinks = styled('div', {
  display: 'flex',
  gap: 'var(--spacing-md)',
  marginTop: 'var(--spacing-sm)',
  
  '@media (max-width: 768px)': {
    justifyContent: 'flex-start',
    marginTop: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-lg)',
    gridColumn: '1 / -1',
    gap: 'var(--spacing-xl)'
  }
});

// 社交媒体图标样式
const SocialIcon = styled('a', {
  color: 'white', // 统一使用白色，与移动端一致
  fontSize: 'var(--font-size-lg)',
  textDecoration: 'none',
  
  '&:hover': {
    color: 'var(--primary-color)'
  },
  
  '@media (max-width: 768px)': {
    fontSize: '24px'
  }
});

// 公司信息样式
const CompanyInfo = styled('div', {
  display: 'none',
  
  '@media (max-width: 768px)': {
    display: 'block',
    gridColumn: '1 / -1',
    fontSize: 'var(--font-size-xs)',
    color: '#8E92A3',
    marginTop: 'var(--spacing-md)'
  }
});

// 底部订阅区域样式
const FooterSubscribe = styled('div', {
  gridColumn: '1 / -1',
  borderTop: '1px solid var(--border-color)',
  marginTop: 'var(--spacing-lg)',
  paddingTop: 'var(--spacing-lg)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  
  '@media (max-width: 768px)': {
    display: 'none' // 移动端不显示订阅区域
  }
});

// 表单样式
const SubscribeForm = styled('form', {
  display: 'flex',
  gap: 'var(--spacing-sm)',
  
  '@media (max-width: 480px)': {
    flexDirection: 'column'
  }
});

// 输入框样式
const Input = styled('input', {
  flex: 1,
  padding: 'var(--spacing-sm) var(--spacing-md)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-sm)',
  backgroundColor: 'var(--background-color)',
  color: 'var(--text-primary)',
  
  '&:focus': {
    outline: 'none',
    borderColor: 'var(--primary-color)'
  }
});

// 按钮样式
const Button = styled('button', {
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--primary-color)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--border-radius-sm)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: 'var(--primary-dark)'
  }
});

// 语言切换区域样式
const LanguageContainer = styled('div', {
  gridColumn: '1 / -1',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '5px', // 设置固定高度为80px
  marginTop: '0',
  gap: '0',
  
  '@media (max-width: 768px)': {
    justifyContent: 'center' // 移动端也显示语言切换区域并居中
  }
});

// 版权信息样式
const Copyright = styled('div', {
  gridColumn: '1 / -1',
  textAlign: 'center',
  marginTop: '0',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-disabled)',
  
  '@media (max-width: 768px)': {
    display: 'block', // 移动端也显示版权信息
    textAlign: 'center'
  }
});

/**
 * 页面底部组件
 * 包含网站导航、社交媒体链接和订阅功能
 */
const Footer: React.FC = () => {
  const { t } = useTranslation();
  
  // 处理订阅表单提交
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 这里只是静态展示，不需要实际处理订阅
    console.log('订阅提交');
  };

  return (
    <FooterContainer>
      <FooterContent>
        {/* 公司栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.company', '公司')}</FooterTitle>
          <FooterLink to="/about">{t('footer.companySubmenu.about', 'About Us')}</FooterLink>
          <FooterLink to="/news/blog">{t('footer.companySubmenu.blog', 'Blog')}</FooterLink>
          <FooterLink to="/help">{t('footer.support', '支持')}</FooterLink>
          <FooterLink to="/help/guides">{t('footer.resources', '资源')}</FooterLink>
          <FooterLink to="/contact">{t('footer.companySubmenu.contact', 'Contact Us')}</FooterLink>
        </FooterColumn>
        
        {/* 产品栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.products', '产品')}</FooterTitle>
          <FooterLink to="/products/cloud-mining">{t('header.productsSubmenu.cloudMining', 'Cloud Mining')}</FooterLink>
          <FooterLink to="/products/mining-hotel">{t('header.productsSubmenu.miningHotel', 'Mining Hotel')}</FooterLink>
          <FooterLink to="/products/asic-shop">{t('header.productsSubmenu.asicShop', 'ASIC Shop')}</FooterLink>
          <FooterLink to="/products/token-pawa">{t('header.productsSubmenu.tokenPAWA', 'Token PAWÂ')}</FooterLink>
          <FooterLink to="/products/whitepaper">{t('header.productsSubmenu.whitepaper', 'Whitepaper')}</FooterLink>
          <FooterLink to="/products/kipa-wallet">{t('header.productsSubmenu.kipaWallet', 'Kipa Wallet')}</FooterLink>
        </FooterColumn>
        
        {/* 资源栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.resources', '资源')}</FooterTitle>
          <FooterLink to="/help/guides">{t('footer.resourcesSubmenu.guides', 'Guides')}</FooterLink>
          <FooterLink to="/help/tutorials">{t('footer.resourcesSubmenu.tutorials', 'Tutorials')}</FooterLink>
          <FooterLink to="/news">{t('footer.resourcesSubmenu.announcements', 'Announcements')}</FooterLink>
          <FooterExternalLink href="https://example.com/whitepaper" target="_blank">{t('footer.resourcesSubmenu.whitepaper', 'Whitepaper')}</FooterExternalLink>
          <FooterExternalLink href="https://example.com/roadmap" target="_blank">{t('footer.resourcesSubmenu.roadmap', 'Roadmap')}</FooterExternalLink>
        </FooterColumn>
        
        {/* 支持栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.support', '支持')}</FooterTitle>
          <FooterLink to="/help">{t('footer.supportSubmenu.helpCenter', 'Help Center')}</FooterLink>
          <FooterLink to="/help/faq">{t('footer.supportSubmenu.faq', 'FAQ')}</FooterLink>
          <FooterLink to="/contact">{t('footer.supportSubmenu.contactSupport', 'Contact Support')}</FooterLink>
          <FooterLink to="/help/knowledge-base">{t('footer.supportSubmenu.knowledgeBase', 'Knowledge Base')}</FooterLink>
          <FooterLink to="/help/troubleshooting">{t('footer.supportSubmenu.troubleshooting', 'Troubleshooting')}</FooterLink>
        </FooterColumn>
        
        {/* 功能特点栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.features', 'FEATURES')}</FooterTitle>
          <FooterLink to="/features/ai-mining">{t('footer.featuresSubmenu.aiMining', 'AI Mining')}</FooterLink>
          <FooterLink to="/features/referral-program">{t('footer.featuresSubmenu.referralProgram', 'Referral Program')}</FooterLink>
        </FooterColumn>
        
        {/* 联系方式栏目 - 暂时隐藏 */}
        <FooterColumn css={{ display: 'none' }}>
          <FooterTitle>{t('footer.contacts', 'CONTACTS')}</FooterTitle>
          <FooterExternalLink href="https://t.me/bemine" target="_blank">{t('footer.contactsSubmenu.telegram', 'Telegram')}</FooterExternalLink>
          <FooterExternalLink href="mailto:marketing@bemine.club">{t('footer.contactsSubmenu.email', 'marketing@bemine.club')}</FooterExternalLink>
        </FooterColumn>
        
        {/* 社交媒体链接 - 暂时隐藏 */}
        <SocialLinks css={{ display: 'none' }}>
          <SocialIcon href="https://t.me/bemine" target="_blank" title={t('footer.social.telegram', 'Telegram')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8.5 12.5L11 15L16 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SocialIcon>
          <SocialIcon href="https://twitter.com/bemine" target="_blank" title={t('footer.social.twitter', 'Twitter')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 4.01C21 4.5 20.02 4.69 19 5C18.39 4.31 17.44 3.8 16.5 3.8C14.64 3.8 13.09 5.25 13.09 7.04C13.09 7.37 13.13 7.7 13.21 8C10.33 7.82 7.74 6.53 6.11 4.5C5.77 5.14 5.5 5.87 5.5 6.66C5.5 8.14 6.31 9.45 7.5 10.2C6.92 10.19 6.35 10.01 5.85 9.73C5.85 9.75 5.85 9.77 5.85 9.79C5.85 11.35 7.06 12.67 8.66 13.05C8.33 13.15 7.98 13.19 7.62 13.19C7.36 13.19 7.12 13.17 6.88 13.13C7.37 14.43 8.58 15.33 10.01 15.36C8.9 16.18 7.5 16.65 6 16.65C5.67 16.65 5.35 16.63 5.04 16.59C6.44 17.48 8.18 16.95 10.06 16.95C16.5 16.95 20 11.85 20 7.39C20 7.21 19.99 7.02 19.98 6.85C21 6.35 21.54 5.79 22.09 5.13C21.44 5.46 20.76 5.68 20.01 5.8C20.77 5.33 21.35 4.61 21.62 3.8" fill="white"/>
            </svg>
          </SocialIcon>
          <SocialIcon href="https://facebook.com/bemine" target="_blank" title={t('footer.social.facebook', 'Facebook')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SocialIcon>
          <SocialIcon href="https://instagram.com/bemine" target="_blank" title={t('footer.social.instagram', 'Instagram')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 2H7C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5931 15.1514 13.8416 15.5297C13.0901 15.9079 12.2384 16.0396 11.4078 15.9059C10.5771 15.7723 9.80976 15.3801 9.21484 14.7852C8.61991 14.1902 8.22773 13.4229 8.09406 12.5922C7.9604 11.7615 8.09206 10.9099 8.47032 10.1584C8.84858 9.40685 9.45418 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87658 12.63 8C13.4789 8.12588 14.2648 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 6.5H17.51" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SocialIcon>
          <SocialIcon href="https://youtube.com/bemine" target="_blank" title={t('footer.social.youtube', 'YouTube')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.5401 6.42C22.4213 5.94541 22.1794 5.51057 21.8387 5.15941C21.4981 4.80824 21.0708 4.55318 20.6001 4.42C18.8801 4 12.0001 4 12.0001 4C12.0001 4 5.12008 4 3.40008 4.46C2.92933 4.59318 2.50206 4.84824 2.16142 5.19941C1.82078 5.55057 1.57887 5.98541 1.46008 6.46C1.14577 8.20556 0.991319 9.97631 1.00008 11.75C0.988993 13.537 1.14345 15.3213 1.46008 17.08C1.59104 17.5398 1.83839 17.9581 2.17872 18.2945C2.51906 18.6308 2.93882 18.8738 3.40008 19C5.12008 19.46 12.0001 19.46 12.0001 19.46C12.0001 19.46 18.8801 19.46 20.6001 19C21.0708 18.8668 21.4981 18.6118 21.8387 18.2606C22.1794 17.9094 22.4213 17.4746 22.5401 17C22.8524 15.2676 23.0068 13.5103 23.0001 11.75C23.0112 9.96295 22.8567 8.1787 22.5401 6.42Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.75 15.02L15.5 11.75L9.75 8.48001V15.02Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SocialIcon>
        </SocialLinks>
        
        {/* 公司信息 - 暂时隐藏 */}
        <CompanyInfo css={{ display: 'none' }}>
          {t('footer.companyInfo.line1', 'ART. Research&Development Ltd. / Company №227776')}<br />
          {t('footer.companyInfo.line2', 'Suite 1, Second Floor, Sound & Vision House, Francis')}<br />
          {t('footer.companyInfo.line3', 'Rachel Str., Victoria, Mahe, Seychelles')}
        </CompanyInfo>
        
        {/* 订阅区域 - 暂时隐藏 */}
        <FooterSubscribe css={{ display: 'none' }}>
          <FooterTitle>{t('footer.subscribe', '订阅')}</FooterTitle>
          <p>{t('footer.subscribeDesc', '订阅我们的新闻通讯以获取最新更新和优惠信息')}</p>
          <SubscribeForm onSubmit={handleSubmit}>
            <Input 
              type="email" 
              placeholder={t('footer.email', '电子邮箱')} 
              required 
            />
            <Button type="submit">
              {t('footer.subscribe', '订阅')}
            </Button>
          </SubscribeForm>
        </FooterSubscribe>
        
        {/* 语言切换区域 */}
        <LanguageContainer>
          <span>{t('common.language', '语言')}: </span>
          <LanguageSwitcher variant="small" />
        </LanguageContainer>
        
        {/* 版权信息 */}
        <Copyright>
          <p>{t('footer.copyright', '© 2025 BITENGINE. 保留所有权利。')}</p>
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 